// src/components/getQuote/GetTraditionalQuote.tsx
import React, { useEffect, useMemo, useState } from "react";
import ViewDetailsTable from "../shared/table/ViewDetailsTable";
import LTLPriceDetailsModal from "../modals/LTLPriceDetailsModal";
import { Fields } from "../../pages/Order-management/CustomerOrder/CustomerOrder-types";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import PriceDetailsModal from "../modals/PriceDetailsModal";
import { buildAllianceChargeLines } from "../../utils/utils";

interface GetTraditionalQuoteProps {
  setFields: (field: any) => void;
  orderType?: string;
  quoteData: any[];
  setIsQuote: (value: boolean) => void;
  fields: Fields;
  isLtlFinalmile?: boolean;
  saveQuotation: (quoteId: string) => void;
  quoteSelectedId?: string;
  formData?: any;
  calledFrom?: any;
  orderTypeRowData?: any;
  setOrderTypeRowData?: (data: any) => void;
  ltlFMData?: any;
  setLtlFMData?: (data: any) => void;
  ltlAccessorialData?: any;
  savedData?: any;
  setIsSelectedParent?: (data: any) => void;
  setQuoteType?: (data: any) => void;
  /**
   * "LTL Only" flag carried on the order/quote. Rendered only for LTL order types
   * and only when the parent screen owns the state, so callers that don't pass
   * these props are unaffected.
   */
  isLtlOnly?: boolean;
  setIsLtlOnly?: (value: boolean) => void;
}

interface SelectedData {
  carrierName?: string;
  scac?: string;
  serviceName?: string;
  orderTypeName?: string;
  transitTime?: number;
  price?: number;
  priceWithMarkUp?: number;
  priceWithMarkUpString?: string;
  markup?: number;
  customerMarkupType?: string;
  miles?: string | number;
  productTransactionId?: string;
  offerId?: string;
  id?: string;
  agentHubId?: string;
  allQuoteId?: string;
  quoteId?: string;
  additionalCharges?: any[];
  priceWithFinalMile?: string | number;
}

const columns = [
  {
    key: "count",
    header: "S.",
    isSortable: false,
    className: "calculateIndex",
  },
  {
    key: "carrierName",
    header: "Carrier Name",
    isSortable: true,
    className: "dark",
  },
  {
    // Blank for plain LTL quotes — only LTL + Final Mile offers carry an agent.
    key: "finalmileCarrierName",
    header: "Agent(Final Mile)",
    isSortable: true,
    className: "dark",
  },
  { key: "scac", header: "Carrier Code", isSortable: true, className: "dark" },
  {
    key: "serviceName",
    header: "Service Name",
    isSortable: true,
    className: "dark",
  },
  {
    key: "orderTypeName",
    header: "Order Type",
    isSortable: true,
    className: "dark",
  },
  {
    key: "transitTime",
    header: "Estimated LTL Transit Days",
    isSortable: true,
    className: "dark",
  },
  {
    key: "priceWithMarkUpString",
    header: "Quote Price",
    isSortable: true,
    className: "dark",
  },
  {
    key: "quoteFrom",
    header: "Quote From",
    isSortable: false,
    hide: true,
    className: "dark",
  },
  {
    key: "displayQuoteFrom",
    header: "Quote From",
    isSortable: false,
    className: "dark",
  },
];

// Single source of truth for a quote's order type, so the "LTL Only" filter and
// the rendered "Order Type" column can never disagree.
const getOrderTypeName = (offer: any): string =>
  offer?.orderTypeName ?? offer?.offeredProductList?.[0]?.productType ?? "";

/**
 * Is this quote a plain LTL leg, i.e. no final-mile component?
 *
 * `orderTypeName` is the declared answer and is what the table's "Order Type"
 * column shows, so it wins whenever it is populated. When it is missing or
 * blank — the mapper already tolerates that — fall back to the shape of the
 * quote itself: every LTL + Final Mile offer (Banyan and internal alike)
 * carries a `finalMileData` block, and plain LTL offers never do. Without the
 * fallback an untyped quote would be silently dropped from the list.
 */
const isLtlOnlyQuote = (offer: any): boolean => {
  const orderTypeName = getOrderTypeName(offer);
  return orderTypeName ? orderTypeName === "LTL" : !offer?.finalMileData;
};

/**
 * Applies the "LTL Only" narrowing to a raw traditional-quote list.
 *
 * Exported so the screens that persist quotes save exactly what the table
 * shows — the predicate must live in one place or the saved quoteArray will
 * drift from the rendered list.
 *
 * When `isLtlOnly` is false this returns the list untouched.
 */
export const filterLtlOnlyQuotes = (
  quotes: any[] = [],
  isLtlOnly?: boolean
): any[] => {
  const list = Array.isArray(quotes) ? quotes : [];
  return isLtlOnly ? list.filter(isLtlOnlyQuote) : list;
};

// ————————————————————————————
// Adapter: Traditional → rows compatible with ViewDetailsTable
// Robust Final‑Mile extraction + table shows TOTAL for LTL+FM
// ————————————————————————————
const mapTraditionalToRows = (arr: any[] = []) => {
  let seq = 0;
  const toMoneyString = (v: any) => {
    const n = Number(v ?? 0);
    return `$${Number.isFinite(n) ? n.toFixed(2) : "0.00"}`;
  };

  return arr.map((offer: any) => {
    const vendor = offer?.primaryVendor || {};
    const vendorName =
      vendor?.preferredName ||
      vendor?.scac ||
      offer?.hub?.agent?.name ||
      offer?.carrierName ||
      "Carrier";
    const scac = vendor?.scac || vendor?.vendorId || offer?.scac || "";
    const internalLTLPopUpDetails = offer?.services?.[0] || {};
    const firstProduct = offer?.offeredProductList?.[0] || {};
    const ship = firstProduct?.shopRQShipment || {};
    const tt =
      ship?.timeInTransit?.transitDays ??
      offer?.transitTime ??
      offer?.transitDays ??
      "1";
    const miles = ship?.timeInTransit?.milesInTransit ?? offer?.miles ?? "";
    let banyanArr: any[] = [];
    if (offer?.quoteFrom === "banyan") {
      banyanArr = [
        { name: "Freight Charge", amount: offer?.customerPrice?.freightCharge },
        { name: "Fuel Surcharge", amount: offer?.customerPrice?.fuelSurcharge },
        ...offer?.customerPrice?.charges,
      ];
    }

    const additionalCharges =
      offer?.quoteFrom === "banyan"
        ? banyanArr
        : firstProduct?.chargeItemList || [];

    // Gate on the raw quoteFrom: the row's own is rewritten further down.
    const isAlliance = offer?.quoteFrom === "alliance";

    // Alliance returns a flat freight charge + accessorial bag instead of a
    // chargeItemList, so `additionalCharges` is empty for it. Leave that field alone —
    // it feeds `costDetails` on select — and carry the display-only breakdown apart.
    const allianceCharges = isAlliance ? buildAllianceChargeLines(offer) : [];

    const serviceName = offer?.serviceName ?? ""; // e.g., "2 Man Threshold"
    const orderTypeName = getOrderTypeName(offer); // "LTL" | "LTL + Final Mile"

    // LTL prices
    const ltlOfferPrice =
      offer?.orderTypeName === "LTL + FinalMile" ? offer?.totalOfferPriceWithMarkupAndFinalMile : firstProduct?.offerPrice?.value ?? offer?.totalOfferPrice?.value ?? 0;

    const priceWithMarkUp =
      offer?.totalOfferPriceWithMarkUp ??
      offer?.totalOfferPrice?.value ??
      firstProduct?.offerPrice?.value ??
      0;

    // —— Final‑Mile extraction (if present) ——
    const fm = offer?.finalMileData;
    const fmRates = fm?.rateDetails || {};

    // Best available service dollar base:
    // prefer explicit basePrice → serviceChargeWithMarkup → serviceCharge
    const serviceChargeWithMarkup = Number(
      fmRates?.serviceChargeWithMarkup ?? fmRates?.serviceCharge ?? 0
    );

    // Dollar FSC: prefer customerFuelSurcharge (dollars) → fuelSurcharge (dollars) → percent of serviceChargeWithMarkup
    const fscPercent = Number(
      fmRates?.customerFscPercent ?? fmRates?.customerFuelSurcharge ?? 0
    );

    const fmFuelDollar = Number(
      offer?.fuel ??
      fmRates?.customerFuelSurcharge ??
      fmRates?.fuelSurcharge ??
      ((serviceChargeWithMarkup || 0) * (fscPercent || 0)) / 100
    );

    // Base price: prefer explicit basePrice → derive from (total - FSC) → fallback to serviceChargeWithMarkup
    const fmQuotePriceRaw = Number(
      fmRates?.total ?? offer?.finalMileQuotePrice ?? fm?.quotePrice ?? 0
    );
    const fmBasePrice = Number(
      offer?.baseRate ??
      fmRates?.basePrice ??
      (Number.isFinite(fmQuotePriceRaw - fmFuelDollar)
        ? fmQuotePriceRaw - fmFuelDollar
        : undefined) ??
      serviceChargeWithMarkup
    );

    // Final mile total (dollars)
    const fmQuotePrice = Number(fmQuotePriceRaw || fmBasePrice + fmFuelDollar);

    const fmOtherCharges =
      fmRates?.additionalOtherCharges ??
      (Array.isArray(fmRates?.additionalOtherChargeDetails)
        ? { calculatedCharges: fmRates?.additionalOtherChargeDetails }
        : undefined);

    const priceWithFinalMile: number =
      Number(
        offer?.totalOfferPriceWithMarkupAndFinalMile ?? offer?.totalPrice
      ) || Number(priceWithMarkUp || 0) + Number(fmQuotePrice || 0);

    // For table: show TOTAL for LTL + Final Mile, else LTL markup price
    const displayPriceNumber =
      orderTypeName === "LTL + Final Mile"
        ? Number(priceWithFinalMile ?? priceWithMarkUp)
        : Number(priceWithMarkUp);
    const row = {
      // table columns
      count: ++seq,
      carrierName: vendorName,
      // Final-mile agent, blank unless this offer has a final-mile leg.
      finalmileCarrierName: offer?.finalMileData?.hub?.agent?.name || "",
      loadId: offer?.loadId,
      scac: offer?.internalLTL ? offer?.hub?.agent?.carrierCode : scac,
      serviceName,
      orderTypeName,
      transitTime: tt,
      priceWithMarkUpString: toMoneyString(displayPriceNumber),
      mode: serviceName || orderTypeName || "LTL",
      markup: offer?.customerMarkUpValue ?? offer?.customerMarkUp ?? 0,
      customerMarkupType: offer?.customerMarkUpType ?? "percent",
      price: ltlOfferPrice,
      priceWithMarkUp, // keep original LTL number for existing flows
      additionalCharges, // LTL charge breakdown
      allianceCharges, // Alliance-only charge breakdown, display only
      lineItems: offer?.lineItems || [],

      // Final‑Mile fields for the modal (dollar values)
      basePrice: fmBasePrice,
      fuelSurcharge: fmFuelDollar,
      finalMileData: offer?.finalMileData,
      additionalOtherCharges: fmOtherCharges, // { calculatedCharges: [...] }
      quotePrice: fmQuotePrice, // presence triggers FM section
      priceWithFinalMile, // overall total (LTL + FM)

      // Priovidence
      accessorialCharges: offer?.accessorialCharges ?? 0,
      totalPrice: offer?.totalPrice ?? offer?.totalOfferPriceWithMarkupAndFinalMile ?? priceWithFinalMile,

      // Alliance identifies a booking with its own pair of ids and returns neither
      // offerId nor quoteId, so without this branch both land undefined and
      // /order/createltlorder is posted with empty shipment ids.
      productTransactionId: offer?.quoteFrom === "armstrong" ? offer?.carrierQuoteId : isAlliance ? offer?.shipmentProductTransactionId : (offer?.productTransactionId ?? offer?.quoteId),
      offerId: offer?.quoteFrom === "armstrong" ? offer?.rateRequestLocator : isAlliance ? offer?.shipmentOfferId : (offer?.offerId ?? offer?.quoteId),
      // Alliance wants these back in the LTL order's quoteDetail exactly as its quote
      // returned them (TraditionalOrder handleSaveLTL).
      allianceQuoteDetail: isAlliance
        ? {
          ServiceLevelID: offer?.ServiceLevelID,
          DeliveryDate: offer?.DeliveryDate,
          DeliveryTime: offer?.DeliveryTime,
        }
        : undefined,
      carrierQuoteId: offer?.carrierQuoteId,
      rateRequestLocator: offer?.rateRequestLocator,
      banyanType: offer?.banyanType,
      quoteFrom: offer?.source ? offer?.source : offer?.banyanType ? offer?.quoteFrom + " - " + offer?.banyanType : offer?.quoteFrom || "Internal",
      miles,
      displayQuoteFrom: offer?.displayQuoteFrom,
      // ids used by save/select flows
      id: firstProduct?.offeredProductId || offer?.offerId,
      agentHubId: offer?.internalLTL ? offer?.hub?.id : vendor?.vendorId,
      allQuoteId: offer?.offerId,
      orderFor: offer?.quoteFrom,
      source: offer?.source ?? "direct",
      selectedFinalMileQuote: offer?.finalMileData,
      quoteId:
        offer?.quoteFrom === "banyan"
          ? offer?.quoteId : offer?.quoteFrom === "providence" ? offer?.quoteDetailId
            : offer?.offerId || internalLTLPopUpDetails?.quoteId,
      internalLTL: offer?.internalLTL,
      ...(offer?.quoteFrom === "banyan" && { orderFor: "banyan" }),
      ...(offer?.internalLTL && {
        services: offer?.internalLTL,
        quotePrice: internalLTLPopUpDetails?.quotePrice,
        fuelSurcharge:
          internalLTLPopUpDetails?.rateDetails?.customerFuelSurcharge || internalLTLPopUpDetails?.rateDetails?.fuelSurcharge || 0.0,
        additionalOrderCharges: internalLTLPopUpDetails?.rateDetails
          ?.finalMilePricing
          ? internalLTLPopUpDetails?.rateDetails?.customerOtherChargeDetails
          : internalLTLPopUpDetails?.rateDetails?.additionalOtherCharges,
        basePrice: internalLTLPopUpDetails?.rateDetails?.basePrice || 0.0,
        agentName: offer?.hub?.agent?.name,
        serviceId: internalLTLPopUpDetails?.id,

      }),
    };

    return row;
  });
};

const GetTraditionalQuote: React.FC<GetTraditionalQuoteProps> = ({
  orderType,
  setFields,
  quoteData,
  setIsQuote,
  fields,
  isLtlFinalmile,
  saveQuotation,
  quoteSelectedId,
  formData,
  calledFrom,
  orderTypeRowData,
  setOrderTypeRowData,
  ltlFMData,
  setLtlFMData,
  ltlAccessorialData,
  savedData,
  setIsSelectedParent,
  setQuoteType,
  isLtlOnly,
  setIsLtlOnly,
}) => {
  // "LTL Only" narrows the list to LTL quotes; unchecked keeps every quote.
  // Filter the source array rather than the mapped rows: `count` is a 1-based
  // index back into this same array (ViewDetailsTable and the price-details
  // modal both read `quoteData[count - 1]`), so the two must stay in step.
  const visibleQuoteData = useMemo(
    () => filterLtlOnlyQuotes(quoteData, isLtlOnly),
    [quoteData, isLtlOnly]
  );

  const rows = useMemo(
    () => mapTraditionalToRows(visibleQuoteData),
    [visibleQuoteData]
  );
  const [viewPriceDetailsModal, setViewPriceDetailsModal] =
    useState<boolean>(false);
  const [viewDetailRowData, setViewDetailRowData] = useState<any>();
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [selectedData, setSelectedData] = useState<SelectedData | null>(null);
  const location = useLocation();
  // const { state } = location;
  // Get Traditional Quote data from Redux if available, otherwise fall back to location state
  const { traditionalQuoteData } = useSelector(
    (state: RootState) => state.locationState
  );
  const { state: locationState } = location;
  const state = traditionalQuoteData || locationState;
  const handleViewDetails = (rowData: any) => {
    setViewDetailRowData(rowData);
    setViewPriceDetailsModal(true);
  };
  const mapCharges = (charges: any[] = []) =>
    charges?.map((item) => ({
      name: item?.customerDescription,
      amount: item?.customerPrice?.value,
    }));

  useEffect(() => {
    setIsSelectedParent && setIsSelectedParent(false);
    setIsSelected(false);
    setSelectedData(null);
    setViewPriceDetailsModal(false);
    setViewDetailRowData(null);
  }, [quoteData]);

  const additionalCharges = (rowData: any): any[] =>
    mapCharges(rowData?.additionalCharges || []);

  const handleSelect = (rowData: any) => {

    setSelectedData(rowData);

    const isQuickQuote =
      typeof state?.page === "string" &&
      state?.page?.toLowerCase() === "quick quote";

    const isBanyan =
      rowData?.banyanType;

    // Rule: if Banyan + Quick Quote => keep isSelected false; else true
    setIsSelected(!(isBanyan && isQuickQuote));

    setIsSelectedParent && setIsSelectedParent(true);
    setFields((prev: any) => ({
      ...prev,
      CarrierName: rowData.carrierName,
      serviceCost: rowData.price,
      scac: rowData.scac,
      distance: rowData.miles,
      offerId: rowData.offerId,
      originAddress: formData?.originAddress?.address,
      destinationAddress: formData?.destinationAddress?.address,
      costDetails: additionalCharges(rowData),
      markup: rowData.markup,
      productTransactionId: rowData.productTransactionId,
      customerMarkupType: rowData.customerMarkupType,
      plProvider: "",
      courierPro1: "",
      courierPro2: "",
      quoteId: rowData?.quoteId,
      orderFor: rowData?.orderFor,
      source: rowData?.source,
      banyanType: rowData?.banyanType,
      loadId: rowData?.loadId,
      finalMileSource: rowData?.finalMileSource || rowData?.finalMileData?.source,
      carrierQuoteId: rowData?.carrierQuoteId,
      rateRequestLocator: rowData?.rateRequestLocator,
      // Always overwritten, so picking another carrier drops an earlier Alliance pick.
      allianceQuoteDetail: rowData?.allianceQuoteDetail,
    }))
    if (rowData?.orderTypeName === "LTL + Final Mile") {
      setQuoteType && setQuoteType("nonTraditional");
    }
  };

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFields((prevData: any) => ({ ...prevData, [name]: value }));
  };

  // useEffect(() => {
  //   if (
  //     (state?.page === "Quick Quote" || state?.page === "Traditional Quote") &&
  //     state?.isSelected
  //   ) {
  //     if (!state?.selectedData?.quoteFrom?.includes("banyan") && state?.page !== "Quick Quote") {
  //       setIsSelected(true);
  //     }
  //     if (state?.selectedData?.quoteFrom?.includes("banyan") && state?.page === "quickQuote") {
  //       setIsSelected(false)
  //     }
  //     // setIsSelected(true);
  //     setIsSelectedParent && setIsSelectedParent(true);
  //     setSelectedData(state?.selectedData);
  //   }
  // }, [state]);
  useEffect(() => {
    if (
      (state?.page === "Quick Quote" || state?.page === "Traditional Quote") &&
      state?.isSelected
    ) {
      const isQuickQuote =
        typeof state?.page === "string" &&
        state?.page?.toLowerCase() === "quick quote";

      const isBanyan =
        typeof state?.selectedData?.quoteFrom === "string" &&
        state?.selectedData?.quoteFrom?.toLowerCase()?.includes("banyan");

      setIsSelected(!(isBanyan && isQuickQuote));
      setIsSelectedParent && setIsSelectedParent(true);
      setSelectedData(state?.selectedData);
    }
  }, [state]);

  return (
    <>
      <div className="formRow">
        {isSelected && (
          <div>
            <div className="getQuoteBox">
              <p className="para">
                {`Carrier: ${selectedData?.carrierName} (${selectedData?.scac})`}
              </p>
              <h3 className="title title--h3">
                Quote Price: $
                {/* {selectedData?.priceWithMarkUp != null
                  ? Number(selectedData.priceWithMarkUp).toFixed(2) */}
                {selectedData?.priceWithFinalMile != null
                  ? Number(selectedData.priceWithFinalMile).toFixed(2)
                  : selectedData?.price != null
                    ? Number(selectedData.price).toFixed(2)
                    : "0.00"}
              </h3>
            </div>

            <section className="form__section">
              <h3 className="form__section__title">LTL Carrier Details</h3>
              <div className="formRow formRow__2">
                <div className="formCol">
                  <div className="form__Field">
                    <label className="form__label">Carrier Name</label>
                    <input
                      type="text"
                      className="form__input form__input--sm form__input--disabled"
                      value={selectedData?.carrierName || ""}
                      disabled
                    />
                  </div>
                </div>
                <div className="formCol">
                  <div className="form__Field">
                    <label className="form__label">Carrier Code</label>
                    <input
                      type="text"
                      className="form__input form__input--sm form__input--disabled"
                      value={selectedData?.scac || ""}
                      disabled
                    />
                  </div>
                </div>
              </div>

              <div className="formRow formRow__2">
                <div className="formCol">
                  <div className="form__Field">
                    <label className="form__label">Origin City</label>
                    <input
                      type="text"
                      className="form__input form__input--sm form__input--disabled"
                      value={""}
                      disabled
                    />
                  </div>
                </div>
                <div className="formCol">
                  <div className="form__Field">
                    <label className="form__label">Service Cost</label>
                    <input
                      type="text"
                      className="form__input form__input--sm form__input--disabled"
                      value={`$${Number(selectedData?.orderTypeName === "LTL + Final Mile" ? selectedData?.priceWithFinalMile
                        : selectedData?.priceWithMarkUp ??
                        selectedData?.price ??
                        0
                      ).toFixed(2)}`}
                      disabled
                    />
                  </div>
                </div>
              </div>

              <div className="formRow formRow__2">
                <div className="formCol">
                  <div className="form__Field">
                    <label className="form__label">3PL Provider</label>
                    <input
                      type="text"
                      id="plProvider"
                      name="plProvider"
                      className="form__input form__input--sm"
                      value={fields.plProvider}
                      onChange={handleFieldChange}
                    />
                  </div>
                </div>
                <div className="formCol">
                  <div className="form__Field">
                    <label className="form__label">Courier Pro No.</label>
                    <input
                      type="text"
                      id="courierPro1"
                      name="courierPro1"
                      className="form__input form__input--sm"
                      value={fields.courierPro1}
                      onChange={handleFieldChange}
                    />
                  </div>
                </div>
              </div>

              <div className="formRow formRow__2 mb_0">
                <div className="formCol">
                  <div className="form__Field">
                    <label className="form__label">BOL No.</label>
                    <input
                      type="text"
                      id="courierPro2"
                      name="courierPro2"
                      className="form__input form__input--sm form__input--disabled"
                      value={""}
                      disabled
                    />
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}

        {!isSelected && (state?.page !== "Quick Quote") && (
          <div className="tableWrap p_0 mt_0">
            {quoteData?.length ? (
              <h3 className="mb_8 title title--h6 form__section__title form__section__title--flex">
                {/* Service: {quoteData?.[0]?.mode|| quoteData?.[0]?.finalMileData?.service?.name || "LTL"}  */}
                <div>
                  {orderType === "LTL" && setIsLtlOnly && (
                    <div className="form__checkbox">
                      <div className="form__checkbox__option mb_0">
                        <input
                          type="checkbox"
                          id="isLtlOnly"
                          autoComplete="off"
                          className="form__checkbox__input"
                          checked={!!isLtlOnly}
                          onChange={(e) => setIsLtlOnly(e.target.checked)}
                        />
                        <label
                          htmlFor="isLtlOnly"
                          className="form__checkbox__label"
                          style={{ color: "#000000" }}
                        >
                          LTL Only
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                <div className="btn__group mb_0">
                  {/* Nothing visible to save once "LTL Only" filters the list empty. */}
                  {quoteSelectedId || !visibleQuoteData.length ? (
                    ""
                  ) : (
                    <button
                      className="btn btn__white"
                      onClick={() => saveQuotation && saveQuotation("fbsf")}
                    >
                      Save Quote
                    </button>
                  )}
                </div>
              </h3>
            ) : (
              ""
            )}
            <ViewDetailsTable
              columns={columns}
              data={rows}
              handleSelect={handleSelect}
              handleViewDetails={handleViewDetails}
              className={"table--OrderLTLTable"}
              quoteSelectedId={quoteSelectedId}
              callingFrom={
                calledFrom === "quickQuoteLTL"
                  ? "quickQuoteLTL"
                  : "Traditional Quote"
              }
              formData={formData}
              isSelected={isSelected}
              setIsSelected={setIsSelected}
              isLtlFm={false}
              setOrderTypeRowData={setOrderTypeRowData}
              ltlFMData={ltlFMData}
              setLtlFMData={setLtlFMData}
              quoteData={visibleQuoteData}
              ltlAccessorialData={ltlAccessorialData}
              setIsSelectedParent={setIsSelectedParent}
            />
          </div>
        )}
      </div>
      {viewPriceDetailsModal &&
        (viewDetailRowData?.internalLTL || viewDetailRowData?.orderFor === "providence") &&
        viewDetailRowData?.orderTypeName !== "LTL + Final Mile" ? (
        <PriceDetailsModal
          setConfirmModal={setViewPriceDetailsModal}
          rowData={viewDetailRowData}
        />
      ) :
        (
          viewPriceDetailsModal &&
          (<LTLPriceDetailsModal
            setConfirmModal={setViewPriceDetailsModal}
            rowData={viewDetailRowData}
            ltlFMData={visibleQuoteData[viewDetailRowData?.count - 1]}
          />)

        )
      }

      {/* {viewPriceDetailsModal &&
        (!viewDetailRowData?.internalLTL ||
          viewDetailRowData?.orderTypeName === "LTL + Final Mile") && (
          <LTLPriceDetailsModal
            setConfirmModal={setViewPriceDetailsModal}
            rowData={viewDetailRowData}
            ltlFMData={quoteData[viewDetailRowData?.count - 1]}
          />
        )} */}
    </>
  );
};

export default GetTraditionalQuote;
